import {
  Table,
  TableBody,
  TableBodyCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@lightspeed/design-system-react'
import React, { FunctionComponent } from 'react'
import { T } from '@transifex/react'
import { formatDateTime, getUserName, TIME_SHORT } from '@vend/utilities'
import { RegisterClosure } from '../../util/register-closure-types'
import { CASH_MOVEMENT_TYPES } from '../../constants'
import { ColouredNumber } from '../util/ColouredNumber'
import { useRegisterClosureManager } from '../../ng-compat/hooks'
import { useCashMovementsTypes } from '../cash-management/utils/useCashMovementTypes'
import { useFormatClosureNumber } from './useFormatClosureNumber'

export interface RegisterClosureSummaryReportProps {
  closure: RegisterClosure
  countedCashInDrawer?: string
  currencySymbol: string
}

interface CashMovementsTableProps {
  closure: RegisterClosure
  currencySymbol: string
}

const CashMovementsTable: FunctionComponent<CashMovementsTableProps> = ({
  closure,
  currencySymbol,
}) => {
  const formatClosureNumber = useFormatClosureNumber()
  const cashMovementTypes = useCashMovementsTypes()

  return (
    <Table className="wr-cash-management-table-container">
      <TableHead>
        <TableRow>
          <TableHeadCell className="vd-no-pad-l">
            <T _str="Time" />
          </TableHeadCell>
          <TableHeadCell>
            <T _str="User" />
          </TableHeadCell>
          <TableHeadCell className="vd-no-pad-l vd-no-pad-r vd-align-right">
            <T
              _str="Amount ({currencySymbol})"
              currencySymbol={currencySymbol}
            />
          </TableHeadCell>
          <TableHeadCell className="vd-no-pad-r">
            <T _str="Reason" />
          </TableHeadCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {closure.cashMovements.map(movement => (
          <TableRow key={movement.id}>
            <TableBodyCell className="vd-no-pad-l">
              {formatDateTime(movement.date, TIME_SHORT)}
            </TableBodyCell>
            <TableBodyCell className="wr-cash-management-table-user">
              {getUserName(movement.user)}
            </TableBodyCell>
            <TableBodyCell className="vd-no-pad-l vd-no-pad-r vd-align-right">
              {movement.type === CASH_MOVEMENT_TYPES.OPENING_CASH_FLOAT ? (
                <span>{formatClosureNumber(movement.amount)}</span>
              ) : (
                <span className={`cash-type--${movement.type}`}>
                  <ColouredNumber
                    number={formatClosureNumber(movement.amount)}
                  />
                </span>
              )}
            </TableBodyCell>
            <TableBodyCell className="vd-no-pad-r wr-cash-management-table-reason">
              <div className="wr-movement-reason">
                <span className="wr-movement-reason-type">
                  {cashMovementTypes(movement.type)}
                </span>
                <span className="wr-movement-reason-note vd-hide-print">
                  {movement.note}
                </span>
              </div>
            </TableBodyCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export const RegisterClosureSummaryReport: FunctionComponent<RegisterClosureSummaryReportProps> = ({
  closure,
  countedCashInDrawer,
  currencySymbol,
}) => {
  const registerClosureManager = useRegisterClosureManager()
  const cashFloat = closure.cashMovements.find(
    movement => movement.type === CASH_MOVEMENT_TYPES.OPENING_CASH_FLOAT
  )
  const formatClosureNumber = useFormatClosureNumber()

  return (
    <Table>
      <TableBody>
        {cashFloat ? (
          <TableRow>
            <td className="vd-tight vd-valign-t">
              <div className="wr-cash-management-table-title">
                <T _str="Cash movements" />
              </div>
            </td>
            <td>
              <CashMovementsTable
                closure={closure}
                currencySymbol={currencySymbol}
              />
            </td>
          </TableRow>
        ) : (
          <TableRow>
            <td>
              <div className="vd-flex vd-flex--justify-between">
                <div>
                  <T _str="Cash movements" />
                </div>
                <div className="wr-cash-mangement-table-total-expected vd-text--secondary vd-text--sub">
                  <T _str="Opening float not set" />
                </div>
              </div>
            </td>
          </TableRow>
        )}
        <TableRow>
          <td colSpan={4}>
            <div className="vd-flex vd-flex--justify-between">
              <div>
                <T _str="Cash payments received" />
              </div>
              <div className="wr-cash-mangement-table-total-expected">
                {formatClosureNumber(closure.cashDrawerTotals.cash_sales_total)}
              </div>
            </div>
          </td>
        </TableRow>
        <TableRow>
          <td colSpan={4}>
            {cashFloat ? (
              <div className="vd-flex vd-flex--justify-between">
                <div>
                  <T _str="Closing float" />
                </div>
                <div className="wr-cash-mangement-table-total-counted">
                  {registerClosureManager.calculateClosingFloat(
                    closure.openingCashFloat,
                    countedCashInDrawer || '0'
                  )}
                </div>
              </div>
            ) : (
              <div className="vd-flex vd-flex--justify-between">
                <div className="wr-cash-mangement-table-total-expected vd-text--secondary vd-text--sub">
                  <T _str="Cash Float not set." />
                </div>
              </div>
            )}
          </td>
        </TableRow>
        <TableRow>
          <td colSpan={4}>
            <div className="vd-flex vd-flex--justify-between">
              <div>
                <strong>
                  <T _str="Cash to bank" />
                </strong>
              </div>
              <div className="wr-cash-mangement-table-total-counted">
                <strong>
                  {formatClosureNumber(
                    registerClosureManager.calculateCashToBank(
                      closure.openingCashFloat,
                      countedCashInDrawer || '0'
                    )
                  )}
                </strong>
              </div>
            </div>
          </td>
        </TableRow>
      </TableBody>
    </Table>
  )
}
